import { useState } from "react";

export default function DiscountModal({
  isOpen,
  onClose,
  discounts = [],
  onApply,
  onRemove,
  onPrint,
  orderId,
  applyingDiscount = false,
  removingDiscount = false,
}) {
const [selected, setSelected] = useState(null);
const [message, setMessage] = useState("");
const [printing, setPrinting] = useState(false);
const [isApplied, setIsApplied] = useState(false);

  if (!isOpen) return null;

  const getDiscountTitle = (discount) => {
    const type = discount?.discountType?.toLowerCase();

    if (type === "percentage") {
      return `${Number(discount.discount || 0).toFixed(0)}% OFF`;
    }

    return `₹${Number(discount.discountAmount || 0).toFixed(0)} OFF`;
  };

  const getDiscountSubtitle = (discount) => {
    const type = discount?.discountType?.toLowerCase();

    if (type === "percentage") {
      return `Maximum discount ₹${Number(discount.discountAmount || 0).toFixed(
        0
      )}`;
    }

    return `Flat discount ₹${Number(discount.discountAmount || 0).toFixed(0)}`;
  };

  const getDiscountId = (discount) => {
    return (
      discount?.discountId ||
      discount?.id ||
      discount?.pk_discount_id ||
      discount?.discount_id ||
      ""
    );
  };

const handleApply = async () => {
  if (!selected || applyingDiscount || isApplied) return;

  setMessage("");

  const result = await onApply(selected);

  if (result) {
    setIsApplied(true);
    setMessage("Discount applied successfully");
  }
};

  const handleRemove = async () => {
    if (!onRemove || removingDiscount) return;

    setMessage("");

    const result = await onRemove();

   if (result) {
  setSelected(null);
  setIsApplied(false);
  setMessage("Discount removed successfully");
}
  };

  const handlePrint = async () => {
    if (!onPrint || printing) return;

    if (!orderId) {
      setMessage("Order ID missing");
      return;
    }

    try {
      setPrinting(true);
      setMessage("");

      const result = await onPrint(orderId);

      if (result) {
        setMessage("Print processed successfully");
      } else {
        setMessage("Print failed");
      }
    } catch (error) {
      console.error("Discount modal print error:", error);
      setMessage("Print failed");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>

    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 montserrat-font-1">
      <div className="w-[760px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* HEADER */}
        <div className="border-b bg-gradient-to-r from-emerald-50 to-green-50 px-6 py-5">
          <h2 className="text-2xl font-bold text-gray-900">
            Available Discounts
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Select one discount offer to apply on this order.
          </p>
        </div>

        <div className="flex">
          {/* LEFT SIDE */}
          <div className="flex-1 p-6">
            {discounts.length === 0 ? (
              <div className="flex h-[220px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50">
                <p className="text-sm font-medium text-gray-500">
                  No discounts available.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {discounts.map((d, i) => {
                  const discountId = getDiscountId(d);
                  const isSelected = getDiscountId(selected) === discountId;
                  const type = d?.discountType?.toLowerCase();

                  return (
                    <div
                      key={discountId || i}
                     onClick={() => {
  if (isApplied) return;

  setSelected(d);
  setMessage("");
}}
                    className={`relative rounded-2xl border p-4 transition-all ${
  isApplied
    ? "cursor-not-allowed opacity-80"
    : "cursor-pointer"
} ${
  isSelected
    ? "border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-100"
    : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40"
}`}
                    >
                      {isSelected && (
                        <div className="absolute right-4 top-4 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white">
                          SELECTED
                        </div>
                      )}

                      <div className="flex items-start gap-4">
                        <div
                          className={`flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-black ${
                            isSelected
                              ? "bg-emerald-500 text-white"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          %
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-black text-gray-900">
                              {getDiscountTitle(d)}
                            </h3>

                            <span
                              className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${
                                type === "percentage"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-purple-100 text-purple-700"
                              }`}
                            >
                              {d.discountType}
                            </span>
                          </div>

                          <p className="mt-1 text-sm font-medium text-gray-600">
                            {getDiscountSubtitle(d)}
                          </p>

                          <div className="mt-3 flex gap-2 text-xs">
                            <span className="rounded-lg bg-white px-3 py-1 font-semibold text-gray-700 ring-1 ring-gray-200">
                              Discount:{" "}
                              {type === "percentage"
                                ? `${Number(d.discount || 0).toFixed(0)}%`
                                : `₹${Number(d.discountAmount || 0).toFixed(
                                    0
                                  )}`}
                            </span>

                            <span className="rounded-lg bg-white px-3 py-1 font-semibold text-gray-700 ring-1 ring-gray-200">
                              Amount: ₹
                              {Number(d.discountAmount || 0).toFixed(0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT SIDE */}
          <div className="w-56 border-l border-dashed border-gray-300 bg-gray-50 p-6">
            <div className="mb-5 rounded-xl bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase text-gray-400">
                Selected Offer
              </p>

              {selected ? (
                <>
                  <p className="mt-2 text-xl font-black text-emerald-600">
                    {getDiscountTitle(selected)}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {getDiscountSubtitle(selected)}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-sm font-medium text-gray-500">
                  No offer selected
                </p>
              )}
            </div>

            {message && (
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-xs font-bold text-emerald-700">
                {message}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
              disabled={!selected || applyingDiscount || removingDiscount || isApplied}
                onClick={handleApply}
                className={`h-12 rounded-xl text-sm font-bold transition ${
                 selected && !applyingDiscount && !removingDiscount && !isApplied
                    ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
               {applyingDiscount
  ? "APPLYING..."
  : isApplied
    ? "APPLIED"
    : "APPLY DISCOUNT"}
              </button>

              <button
                disabled={!onPrint || printing || applyingDiscount || removingDiscount}
                onClick={handlePrint}
                className={`h-12 rounded-xl text-sm font-bold transition ${
                  onPrint && !printing && !applyingDiscount && !removingDiscount
                    ? "bg-blue-500 text-white hover:bg-blue-600 shadow-sm"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {printing ? "PRINTING..." : "PRINT"}
              </button>

              <button
                disabled={!onRemove || applyingDiscount || removingDiscount}
                onClick={handleRemove}
                className={`h-12 rounded-xl border border-gray-300 bg-white text-sm font-bold text-gray-700 hover:bg-gray-100 ${
                  !onRemove || applyingDiscount || removingDiscount
                    ? "cursor-not-allowed opacity-60"
                    : ""
                }`}
              >
                {removingDiscount ? "REMOVING..." : "REMOVE"}
              </button>

              <button
                disabled={applyingDiscount || removingDiscount || printing}
                onClick={() => {
                  setMessage("");
                  onClose();
                }}
                className="h-12 rounded-xl border border-gray-300 bg-white text-sm font-bold text-gray-900 hover:bg-gray-100"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}